import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './stores/auth.js';
import { useProjects } from './stores/projects.js';
import { useMessages } from './stores/messages.js';
import { useUi } from './stores/ui.js';
import { startReplay } from './lib/offline/replay.js';
import { useKeyboard } from './hooks/useKeyboard.js';
import { useIsMobile } from './hooks/useIsMobile.js';
import { Sidebar } from './components/Sidebar.js';
import { SidebarContent } from './components/SidebarContent.js';
import { MobileTopBar } from './components/MobileTopBar.js';
import { ShortcutsOverlay } from './components/ShortcutsOverlay.js';
import { QuickAddModal } from './components/QuickAddModal.js';
import { TaskDetailModal } from './components/TaskDetailModal.js';
import { ProjectFormModal } from './components/ProjectFormModal.js';
import { SearchModal } from './components/SearchModal.js';
import { CourseAddModal } from './components/CourseAddModal.js';
import { HabitFormModal } from './components/HabitFormModal.js';
import { HabitStatsModal } from './components/HabitStatsModal.js';
import { CatalogItemModal } from './components/CatalogItemModal.js';
import { CarrefourCookiesModal } from './components/CarrefourCookiesModal.js';
import { LafourcheCredentialsModal } from './components/LafourcheCredentialsModal.js';
import { Toast } from './components/Toast.js';
import { SettingsModal } from './components/SettingsModal.js';
import { NotificationsModal } from './components/NotificationsModal.js';
import { SwUpdateBanner } from './components/SwUpdateBanner.js';
import { Drawer } from './ui/Drawer.js';
import { BottomNav } from './ui/BottomNav.js';
import { Login } from './views/Login.js';
import { Today } from './views/Today.js';
import { Project } from './views/Project.js';
import { Board } from './views/Board.js';
import { Courses } from './views/Courses.js';
import { Tracker } from './views/Tracker.js';
import { Calories } from './views/Calories.js';
import { Projects } from './views/Projects.js';
import { Messages } from './views/Messages.js';
import { Message } from './views/Message.js';

function RequireAuth({ children }: { children: React.ReactNode }): React.ReactElement {
  const token = useAuth((s) => s.token);
  const location = useLocation();
  // The destination travels with the redirect: a widget tap or a push notification
  // lands on /login when the session is cold, and must resume where it was headed
  // instead of dumping the user on /today.
  if (!token) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children}</>;
}

function AppShell(): React.ReactElement {
  const [showShortcuts, setShowShortcuts] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const loadProjects = useProjects((s) => s.load);
  const loadMessages = useMessages((s) => s.load);
  const openQuickAdd = useUi((s) => s.openQuickAdd);
  const openSearch = useUi((s) => s.openSearch);
  const drawerOpen = useUi((s) => s.drawerOpen);
  const closeDrawer = useUi((s) => s.closeDrawer);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // `.avec-barre-bas` (index.css) remonte le bouton flottant et les notifications au-dessus de
  // la barre d'onglets. Posée sur <html> : les notifications sont ancrées à la fenêtre.
  useEffect(() => {
    document.documentElement.classList.toggle('avec-barre-bas', isMobile);
    return () => document.documentElement.classList.remove('avec-barre-bas');
  }, [isMobile]);

  // Le rejeu des écritures faites hors-ligne (ADR 0010). Sans Background Sync sur iOS,
  // le démarrage de l'app *est* un des deux déclencheurs, pas un filet de sécurité.
  useEffect(() => startReplay(), []);

  // Ce qui alimente le badge de la cloche. Le rafraîchissement au retour au premier plan
  // est le geste exact d'une notification tapée : l'app revient, le compte doit être juste.
  useEffect(() => {
    loadMessages();
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') loadMessages();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadMessages]);

  useKeyboard([
    {
      key: 'q',
      handler: () => {
        const m = location.pathname.match(/^\/p\/([^/]+)/);
        openQuickAdd(m ? m[1] : null);
      },
    },
    { key: '/', handler: () => openSearch() },
    { key: 'k', modifier: 'meta', handler: () => openSearch(), allowInInput: true },
    { key: 't', handler: () => navigate('/today') },
    { key: 'b', handler: () => navigate('/board') },
    {
      key: '?',
      handler: () => setShowShortcuts((v) => !v),
      allowInInput: true,
    },
    {
      // Hors dialogue seulement (`useKeyboard`) : un dialogue ferme sur Escape de lui-même.
      key: 'escape',
      handler: () => (document.activeElement as HTMLElement | null)?.blur(),
      allowInInput: true,
    },
  ]);

  const overlays = (
    <>
      <QuickAddModal />
      <TaskDetailModal />
      <ProjectFormModal />
      <SearchModal />
      <CourseAddModal />
      <HabitFormModal />
      <HabitStatsModal />
      <CatalogItemModal />
      <CarrefourCookiesModal />
      <LafourcheCredentialsModal />
      <SettingsModal />
      <NotificationsModal />
      <ShortcutsOverlay open={showShortcuts} onClose={() => setShowShortcuts(false)} />
      <Toast />
    </>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col min-h-screen pl-safe pr-safe">
        <MobileTopBar />
        <SwUpdateBanner />
        <main className="flex-1 pb-safe-plus-nav">
          <Routes>
            <Route path="/" element={<Navigate to="/today" replace />} />
            <Route path="/today" element={<Today />} />
            <Route path="/board" element={<Board />} />
            {/* La vue s'appelle Upcoming, sa route /board : l'URL devinée reste servie. */}
            <Route path="/upcoming" element={<Navigate to="/board" replace />} />
            <Route path="/courses" element={<Courses />} />
            {/* Le Catalogue est un onglet de Courses depuis l'ADR 0007 ; l'ancienne
                URL reste servie pour les favoris. */}
            <Route path="/catalogue" element={<Navigate to="/courses?tab=catalogue" replace />} />
            <Route path="/projets" element={<Projects />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/messages/:messageId" element={<Message />} />
            <Route path="/tracker" element={<Tracker />} />
            <Route path="/calories" element={<Calories />} />
            <Route path="/p/:projectId" element={<Project />} />
            <Route path="*" element={<Navigate to="/today" replace />} />
          </Routes>
        </main>
        <BottomNav />
        <Drawer open={drawerOpen} onClose={closeDrawer} label="Menu">
          <SidebarContent mobile onNavigate={closeDrawer} />
        </Drawer>
        {overlays}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <SwUpdateBanner />
      <div className="flex flex-1 min-h-0">
        <Sidebar />
        <main className="flex-1 overflow-y-auto">
          <Routes>
            <Route path="/" element={<Navigate to="/today" replace />} />
            <Route path="/today" element={<Today />} />
            <Route path="/board" element={<Board />} />
            {/* La vue s'appelle Upcoming, sa route /board : l'URL devinée reste servie. */}
            <Route path="/upcoming" element={<Navigate to="/board" replace />} />
            <Route path="/courses" element={<Courses />} />
            {/* Le Catalogue est un onglet de Courses depuis l'ADR 0007 ; l'ancienne
                URL reste servie pour les favoris. */}
            <Route path="/catalogue" element={<Navigate to="/courses?tab=catalogue" replace />} />
            <Route path="/projets" element={<Projects />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/messages/:messageId" element={<Message />} />
            <Route path="/tracker" element={<Tracker />} />
            <Route path="/calories" element={<Calories />} />
            <Route path="/p/:projectId" element={<Project />} />
            <Route path="*" element={<Navigate to="/today" replace />} />
          </Routes>
        </main>
      </div>
      {overlays}
    </div>
  );
}

export function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
